import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import { Badge, Button, Form, Spinner } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import {
  faBan,
  faCheckSquare,
  faExternalLinkAlt,
  faObjectGroup,
  faQuestionCircle,
  faSave,
  faSquare,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  auroMetalSaurus,
  backgroundGrey5,
  bootstrapDanger,
  lightRed,
  positiveGreen,
  positiveGreenBackground,
  red,
  softBlue,
  softBlueLight,
  squidInk,
  warmGray,
  white,
} from 'lib/css/colors';
import { BrandVerificationStatus } from 'lib/enums/BrandVerificationStatus';
import BrandSearchPanel from 'components/manage_brands/brand_verify/BrandSearchPanel';
import {
  BLANK_WARNING_HINT,
  ManufacturerMode,
} from 'components/manage_brands/brand_verify/constants';
import DistributorsList from 'components/manage_brands/brand_verify/DistributorsList';
import ExistingManufacturerFields from 'components/manage_brands/brand_verify/ExistingManufacturerFields';
import FieldLabel from 'components/manage_brands/brand_verify/FieldLabel';
import ManufacturerPanel from 'components/manage_brands/brand_verify/ManufacturerPanel';
import MergeBrandsModal from 'components/manage_brands/MergeBrandsModal';
import SampleProductsList from 'components/manage_brands/brand_verify/SampleProductsList';
import sharedStyles from 'components/manage_brands/brand_verify/sharedStyles';
import { getURL, GLOBAL_MANUFACTURERS_GET_BY_ID } from 'lib/networking/endpoints';
import useGet from 'lib/hooks/useGet';
import { isBlank } from 'lib/utils/stringUtils';

function DuplicateBrandsCard({
  sortedDuplicateEntries = [],
  duplicateBrandsList = [],
  brandsById = {},
  brandsLoading = false,
  brandsInitialLoading = false,
  currentBrandId = null,
  selectedBrandId = null,
  onSelectBrandId = null,
  onSaveChanges = null,
  onAfterMergeDuplicates = null,
  onUnverifyBrands = null,
  selectedBrand = null,
  selectedBrandLoading = false,
  saving = false,
  unverifying = false,
  updatingStatus = false,
  skipping = false,
  manualDuplicateBrandIds = [],
  onAddManualDuplicate = null,
  onRemoveManualDuplicate = null,
}) {
  const [name, setName] = useState('');
  const [brandUrl, setBrandUrl] = useState('');
  const [manufacturerMode, setManufacturerMode] = useState(ManufacturerMode.NEW);
  const [manufacturerId, setManufacturerId] = useState(null);
  const [manufacturerName, setManufacturerName] = useState('');
  const [manufacturerUrl, setManufacturerUrl] = useState('');
  const [manufacturerSavedName, setManufacturerSavedName] = useState('');
  const [manufacturerSavedUrl, setManufacturerSavedUrl] = useState('');
  const [newManufacturerName, setNewManufacturerName] = useState('');
  const [newManufacturerUrl, setNewManufacturerUrl] = useState('');
  const [seedBrandId, setSeedBrandId] = useState(null);
  const [unverifyBrandIds, setUnverifyBrandIds] = useState([]);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeList, setMergeList] = useState([]);
  // Optimistically hide chips for brands just merged away. The backend merge runs in a daemon
  // thread (see BrandService.merge_brands), so a refetch immediately after the API returns will
  // still see the child brand. This local set lets us drop the chip right away.
  const [mergedAwayBrandIds, setMergedAwayBrandIds] = useState([]);

  useEffect(() => {
    if (selectedBrand) {
      const mfr = selectedBrand.global_manufacturer;
      setName(selectedBrand.name ?? '');
      setBrandUrl(selectedBrand.brand_url ?? '');
      setManufacturerMode(mfr?.id ? ManufacturerMode.EXISTING : ManufacturerMode.NEW);
      setManufacturerId(mfr?.id ?? null);
      setManufacturerName(mfr?.name ?? '');
      setManufacturerUrl(mfr?.manufacturer_url ?? '');
      setManufacturerSavedName(mfr?.name ?? '');
      setManufacturerSavedUrl(mfr?.manufacturer_url ?? '');
      setNewManufacturerName('');
      setNewManufacturerUrl('');
      setSeedBrandId(selectedBrand.id);
    } else {
      setName('');
      setBrandUrl('');
      setManufacturerMode(ManufacturerMode.NEW);
      setManufacturerId(null);
      setManufacturerName('');
      setManufacturerUrl('');
      setManufacturerSavedName('');
      setManufacturerSavedUrl('');
      setNewManufacturerName('');
      setNewManufacturerUrl('');
      setSeedBrandId(null);
    }
  }, [selectedBrand]);

  useEffect(() => {
    setUnverifyBrandIds([]);
    setMergedAwayBrandIds([]);
  }, [currentBrandId]);

  useEffect(() => {
    setUnverifyBrandIds(prev =>
      prev.filter(
        id => brandsById[String(id)]?.verification_status === BrandVerificationStatus.VERIFIED,
      ),
    );
  }, [brandsById]);

  const originalManufacturerId = selectedBrand?.global_manufacturer?.id ?? null;
  const isExistingMode = manufacturerMode === ManufacturerMode.EXISTING;
  const isNewMode = manufacturerMode === ManufacturerMode.NEW;
  const pickedDifferentManufacturer =
    isExistingMode && manufacturerId != null && manufacturerId !== originalManufacturerId;
  const { data: pickedMfrData, loading: pickedMfrLoading } = useGet(
    pickedDifferentManufacturer
      ? getURL(GLOBAL_MANUFACTURERS_GET_BY_ID, { manufacturer_id: manufacturerId })
      : '',
    {},
    !pickedDifferentManufacturer,
  );

  useEffect(() => {
    const fetched = pickedMfrData?.manufacturer;
    if (fetched && fetched.id === manufacturerId) {
      const fetchedName = fetched.name || '';
      const fetchedUrl = fetched.manufacturer_url || '';
      setManufacturerName(fetchedName);
      setManufacturerUrl(fetchedUrl);
      setManufacturerSavedName(fetchedName);
      setManufacturerSavedUrl(fetchedUrl);
    }
  }, [pickedMfrData, manufacturerId]);

  const handlePickManufacturer = option => {
    const id = option?.value || null;
    const optionLabel = option?.label || '';

    setManufacturerMode(ManufacturerMode.EXISTING);

    if (id != null && id === originalManufacturerId) {
      const mfr = selectedBrand?.global_manufacturer;
      setManufacturerId(id);
      setManufacturerName(mfr?.name ?? '');
      setManufacturerUrl(mfr?.manufacturer_url ?? '');
      setManufacturerSavedName(mfr?.name ?? '');
      setManufacturerSavedUrl(mfr?.manufacturer_url ?? '');
      return;
    }

    setManufacturerId(id);
    setManufacturerName(id ? optionLabel : '');
    setManufacturerUrl('');
    setManufacturerSavedName('');
    setManufacturerSavedUrl('');
  };

  const handleActivatePanel = mode => {
    if (manufacturerMode !== mode) {
      setManufacturerMode(mode);
    }
  };

  const toggleUnverifyBrandId = brandId => {
    setUnverifyBrandIds(prev =>
      prev.includes(brandId) ? prev.filter(id => id !== brandId) : [...prev, brandId],
    );
  };

  const autoDuplicateIds = sortedDuplicateEntries.map(([id]) => Number(id));
  const autoDuplicateIdSet = new Set(autoDuplicateIds);
  const displayEntries = [
    ...sortedDuplicateEntries.map(([id, payload]) => ({
      brandId: Number(id),
      ceScore: payload?.ce_score,
      brandName: payload?.brand_name,
      manual: false,
    })),
    ...manualDuplicateBrandIds
      .filter(id => !autoDuplicateIdSet.has(Number(id)))
      .map(id => ({
        brandId: Number(id),
        ceScore: null,
        brandName: brandsById[String(id)]?.name ?? '',
        manual: true,
      })),
  ];
  const count = displayEntries.length;
  const detailsReady = selectedBrand != null && seedBrandId === selectedBrand.id;
  const parentBusy = unverifying || updatingStatus || skipping;

  const excludedBrandIds = [
    ...(currentBrandId != null ? [Number(currentBrandId)] : []),
    ...displayEntries.map(entry => Number(entry.brandId)),
  ];

  const handleAddBrandFromPanel = id => {
    if (!id || id === currentBrandId) return;
    onAddManualDuplicate?.(Number(id));
  };

  return (
    <div className={classNames(css(sharedStyles.card), 'p-4')}>
      <div
        className={classNames(
          css(sharedStyles.cardTopRow, styles.cardTopRow),
          'd-flex align-items-start mb-3',
        )}
      >
        <div className={css(styles.leftColumn)}>
          <div className={css(styles.titleRow)}>
            <div className={css(sharedStyles.fieldLabel, styles.fieldLabelInline)}>
              POSSIBLE DUPLICATE BRANDS
            </div>
            <div className={css(sharedStyles.heading)}>
              {count} {count === 1 ? 'candidate' : 'candidates'}
            </div>
          </div>
          {count === 0 ? (
            <div className={css(styles.emptyMessage)}>No duplicate brand candidates</div>
          ) : (
            <div className={css(styles.chipsContainer)}>
              {brandsInitialLoading && (
                <Spinner animation="border" size="sm" className={css(styles.chipsLoadingSpinner)} />
              )}
              {displayEntries.map(({ brandId, ceScore, brandName, manual }) => {
                // Optimistically hide chips for brands the user just merged away.
                if (mergedAwayBrandIds.includes(Number(brandId))) {
                  return null;
                }
                const fetchedBrand = brandsById[String(brandId)];
                // Brand missing from a completed fetch => deleted (e.g. merged away); skip chip.
                if (!fetchedBrand && !brandsLoading && !brandsInitialLoading) {
                  return null;
                }
                const isSelected = String(selectedBrandId) === String(brandId);
                const isChecked = unverifyBrandIds.includes(Number(brandId));
                const isStatusLoading = !fetchedBrand && brandsLoading;
                const isVerified =
                  fetchedBrand?.verification_status === BrandVerificationStatus.VERIFIED;
                const showCheckbox = !isStatusLoading && isVerified;
                const displayName = fetchedBrand?.name ?? brandName;

                const chipColorStyle = isStatusLoading
                  ? styles.duplicateBrandChipLoading
                  : isVerified
                  ? styles.duplicateBrandChipVerified
                  : styles.duplicateBrandChipUnverified;
                const chipSelectedStyle = isVerified
                  ? styles.duplicateBrandChipVerifiedSelected
                  : styles.duplicateBrandChipUnverifiedSelected;

                return (
                  <span
                    key={brandId}
                    className={css(
                      styles.duplicateBrandChip,
                      chipColorStyle,
                      showCheckbox && isChecked && styles.duplicateBrandChipChecked,
                      !isStatusLoading && isSelected && chipSelectedStyle,
                    )}
                    onClick={() =>
                      onSelectBrandId?.(
                        String(selectedBrandId) === String(brandId) ? null : Number(brandId),
                      )
                    }
                  >
                    {showCheckbox && (
                      <FontAwesomeIcon
                        icon={isChecked ? faCheckSquare : faSquare}
                        className={css(styles.chipCheckbox)}
                        onClick={e => {
                          e.stopPropagation();
                          toggleUnverifyBrandId(Number(brandId));
                        }}
                      />
                    )}
                    <span className={css(isSelected && styles.chipNameSelected)}>
                      {displayName}
                    </span>
                    {manual ? (
                      <span
                        className={css(styles.chipScore, isSelected && styles.chipScoreSelected)}
                        title="Manually added"
                      >
                        +
                      </span>
                    ) : (
                      <span
                        className={css(styles.chipScore, isSelected && styles.chipScoreSelected)}
                      >
                        {Number(ceScore).toFixed(2)}
                      </span>
                    )}
                    {manual && (
                      <FontAwesomeIcon
                        icon={faTimes}
                        className={css(styles.chipRemoveIcon)}
                        onClick={e => {
                          e.stopPropagation();
                          onRemoveManualDuplicate?.(Number(brandId));
                        }}
                      />
                    )}
                  </span>
                );
              })}
            </div>
          )}
          {count > 0 && (
            <div
              className={classNames(css(styles.mergeVerifiedRow), 'd-flex justify-content-start')}
            >
              <Button
                variant="danger"
                className={css(styles.mergeVerifiedButton)}
                onClick={() => {
                  setMergeList(duplicateBrandsList);
                  setShowMergeModal(true);
                }}
                disabled={brandsLoading || duplicateBrandsList.length < 2 || saving || parentBusy}
              >
                <FontAwesomeIcon icon={faObjectGroup} className="mr-2" />
                Merge Duplicate Brands
              </Button>
              <Button
                className={css(styles.unverifyBrandsButton)}
                onClick={() => onUnverifyBrands?.(unverifyBrandIds)}
                disabled={!unverifyBrandIds.length || saving || parentBusy}
              >
                <FontAwesomeIcon icon={faBan} className="mr-2" />
                Unverify Brands{unverifyBrandIds.length ? ` (${unverifyBrandIds.length})` : ''}
              </Button>
            </div>
          )}
        </div>
        <div className={css(styles.topRightColumn)}>
          <div className={css(styles.searchWrapper)}>
            <div className={css(sharedStyles.fieldLabel, styles.fieldLabelInline)}>
              ADD DUPLICATE BRAND
            </div>
            <BrandSearchPanel
              onPick={handleAddBrandFromPanel}
              excludedBrandIds={excludedBrandIds}
            />
          </div>
        </div>
      </div>

      <div className={css(styles.detailsSection)}>
        {selectedBrandLoading || (selectedBrandId && !detailsReady) ? (
          <div className={css(styles.detailsState)}>
            <Spinner animation="border" size="sm" className="mr-2" />
            <span>Loading brand details</span>
          </div>
        ) : !selectedBrandId ? (
          <div className={css(styles.detailsState, styles.placeholderMessage)}>
            Select a duplicate brand to view details
          </div>
        ) : (
          <>
            <div className="mb-3">
              <div className={css(styles.brandNameLabelRow)}>
                <FieldLabel label="BRAND NAME" className={css(styles.brandNameLabel)} />
                <a
                  href={`/enrich-products/brand-details/${selectedBrandId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={css(sharedStyles.idLink, styles.brandIdLinkSpacing)}
                >
                  ID {selectedBrandId}
                </a>
              </div>
              <Form.Control
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                className={css(sharedStyles.textInput, sharedStyles.boldInput)}
              />
            </div>
            <div className="mb-3">
              <FieldLabel
                label="BRAND URL"
                warning={isBlank(brandUrl) ? BLANK_WARNING_HINT : null}
              />
              <div className={css(sharedStyles.urlInputWrapper)}>
                <FontAwesomeIcon
                  icon={faQuestionCircle}
                  className={css(sharedStyles.urlInputIcon)}
                />
                <Form.Control
                  type="text"
                  value={brandUrl}
                  onChange={e => setBrandUrl(e.target.value)}
                  className={css(sharedStyles.textInput, sharedStyles.urlInput)}
                />
                {brandUrl && (
                  <a
                    href={brandUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={css(styles.urlOpenIcon)}
                  >
                    <FontAwesomeIcon icon={faExternalLinkAlt} />
                  </a>
                )}
              </div>
            </div>
            <div className="mb-3">
              <FieldLabel label="MANUFACTURER" />
              <div className={css(styles.mfrPanelGroup)}>
                <ManufacturerPanel
                  active={isNewMode}
                  onActivate={() => handleActivatePanel(ManufacturerMode.NEW)}
                  headerLabel="NEW MANUFACTURER"
                  headerExtras={<Badge className={css(styles.noManufacturerIdBadge)}>NEW</Badge>}
                >
                  <div className="mb-2">
                    <FieldLabel
                      label="Manufacturer Name"
                      warning={
                        isNewMode && isBlank(newManufacturerName) ? BLANK_WARNING_HINT : null
                      }
                    />
                    <Form.Control
                      type="text"
                      value={newManufacturerName}
                      onChange={e => setNewManufacturerName(e.target.value)}
                      onFocus={() => handleActivatePanel(ManufacturerMode.NEW)}
                      className={css(sharedStyles.textInput)}
                    />
                  </div>
                  <div>
                    <div className={css(sharedStyles.urlLabelRow)}>
                      <FieldLabel
                        label="Manufacturer URL"
                        warning={
                          isNewMode && isBlank(newManufacturerUrl) ? BLANK_WARNING_HINT : null
                        }
                      />
                      {newManufacturerUrl && (
                        <a
                          href={newManufacturerUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          className={css(sharedStyles.urlOpenIcon)}
                        >
                          <FontAwesomeIcon icon={faExternalLinkAlt} />
                        </a>
                      )}
                    </div>
                    <Form.Control
                      type="text"
                      value={newManufacturerUrl}
                      onChange={e => setNewManufacturerUrl(e.target.value)}
                      onFocus={() => handleActivatePanel(ManufacturerMode.NEW)}
                      className={css(sharedStyles.textInput)}
                    />
                  </div>
                </ManufacturerPanel>
                <ManufacturerPanel
                  active={isExistingMode}
                  highlight={manufacturerId != null}
                  onActivate={() => handleActivatePanel(ManufacturerMode.EXISTING)}
                  headerLabel="EXISTING MANUFACTURER"
                  headerExtras={
                    manufacturerId ? (
                      <a
                        href={`/enrich-products/manufacturer-details/${manufacturerId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        className={css(sharedStyles.idLink)}
                      >
                        ID {manufacturerId}
                      </a>
                    ) : null
                  }
                >
                  <ExistingManufacturerFields
                    manufacturerId={manufacturerId}
                    name={manufacturerName}
                    url={manufacturerUrl}
                    savedName={manufacturerSavedName}
                    savedUrl={manufacturerSavedUrl}
                    loading={pickedMfrLoading}
                    filterKey={selectedBrandId}
                    onPickManufacturer={handlePickManufacturer}
                    onChangeName={setManufacturerName}
                    onChangeUrl={setManufacturerUrl}
                    onFieldFocus={() => handleActivatePanel(ManufacturerMode.EXISTING)}
                    warningVerb="save changes"
                    disabledByDefault={manufacturerId == null}
                    nameBlankWarning={
                      isExistingMode && manufacturerId != null && isBlank(manufacturerName)
                    }
                    urlBlankWarning={
                      isExistingMode && manufacturerId != null && isBlank(manufacturerUrl)
                    }
                  />
                </ManufacturerPanel>
              </div>
            </div>
            <DistributorsList
              brand={selectedBrand}
              totalProductCount={selectedBrand?.product_count}
              defaultVisibleCount={5}
            />
            <SampleProductsList brand={selectedBrand} />
            {/* implemented in a follow-up PR */}
            <div
              className={classNames(
                css(sharedStyles.cardActionBar, styles.actionBarSpacing),
                'd-flex justify-content-end align-items-center',
              )}
            >
              <Button
                variant="success"
                className={css(sharedStyles.primaryGreenButton)}
                disabled={saving || pickedMfrLoading || parentBusy}
                onClick={() =>
                  onSaveChanges?.({
                    brandId: selectedBrandId,
                    name,
                    brandUrl,
                    manufacturerMode,
                    manufacturerId,
                    existingManufacturerName: manufacturerName,
                    existingManufacturerUrl: manufacturerUrl,
                    existingManufacturerSavedName: manufacturerSavedName,
                    existingManufacturerSavedUrl: manufacturerSavedUrl,
                    newManufacturerName,
                    newManufacturerUrl,
                  })
                }
              >
                {saving ? (
                  <Spinner animation="border" size="sm" className="mr-2" />
                ) : (
                  <FontAwesomeIcon icon={faSave} className="mr-2" />
                )}
                Save Changes
              </Button>
            </div>
          </>
        )}
      </div>
      {showMergeModal ? (
        <MergeBrandsModal
          mergeList={mergeList}
          setMergeList={setMergeList}
          onHide={() => setShowMergeModal(false)}
          onMerge={() => {
            // The merge modal enforces exactly one verified brand (the parent); every other
            // brand in mergeList is an unverified child that the backend will delete.
            const mergedChildIds = mergeList
              .filter(b => b.verification_status !== BrandVerificationStatus.VERIFIED)
              .map(b => b.id);
            setMergedAwayBrandIds(prev =>
              Array.from(new Set([...prev, ...mergedChildIds.map(Number)])),
            );
            onAfterMergeDuplicates?.(mergedChildIds);
          }}
        />
      ) : null}
    </div>
  );
}

const styles = StyleSheet.create({
  cardTopRow: {
    gap: 16,
  },
  leftColumn: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
    gap: 12,
  },
  titleRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    columnGap: 10,
    rowGap: 4,
  },
  fieldLabelInline: {
    marginBottom: 0,
  },
  brandId: {
    color: auroMetalSaurus,
    fontSize: '0.85rem',
    fontWeight: 600,
    textAlign: 'right',
  },
  topRightColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 8,
    width: 380,
    flexShrink: 0,
  },
  searchWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  brandNameLabelRow: {
    display: 'flex',
    alignItems: 'baseline',
    marginBottom: 6,
  },
  brandNameLabel: {
    marginBottom: 0,
  },
  brandIdLinkSpacing: {
    marginLeft: 12,
  },
  chipsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    alignContent: 'flex-start',
    gap: 8,
    flex: 1,
  },
  chipsLoadingSpinner: {
    color: auroMetalSaurus,
  },
  mergeVerifiedRow: {
    marginTop: 4,
    gap: 8,
  },
  duplicateBrandChip: {
    padding: '4px 12px',
    borderRadius: 16,
    fontSize: '0.8rem',
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
    border: '1px solid transparent',
  },
  duplicateBrandChipVerified: {
    backgroundColor: positiveGreenBackground,
    color: positiveGreen,
  },
  duplicateBrandChipVerifiedSelected: {
    backgroundColor: positiveGreen,
    color: white,
  },
  duplicateBrandChipUnverified: {
    backgroundColor: softBlueLight,
    color: softBlue,
  },
  duplicateBrandChipUnverifiedSelected: {
    backgroundColor: softBlue,
    color: white,
  },
  duplicateBrandChipLoading: {
    backgroundColor: backgroundGrey5,
    color: auroMetalSaurus,
  },
  duplicateBrandChipChecked: {
    border: `1px solid ${positiveGreen}`,
  },
  chipCheckbox: {
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
  chipNameSelected: {
    color: white,
  },
  chipScore: {
    color: warmGray,
    fontWeight: 500,
    fontSize: '0.75rem',
  },
  chipScoreSelected: {
    color: white,
    opacity: 0.85,
  },
  chipRemoveIcon: {
    fontSize: '0.75rem',
    cursor: 'pointer',
    opacity: 0.7,
    ':hover': {
      opacity: 1,
    },
  },
  emptyMessage: {
    color: warmGray,
    fontSize: '0.9rem',
    fontStyle: 'italic',
  },
  detailsSection: {
    marginTop: 36,
    paddingTop: 24,
    borderTop: `1px solid ${backgroundGrey5}`,
  },
  detailsState: {
    color: warmGray,
    fontSize: '0.9rem',
    display: 'flex',
    alignItems: 'center',
    padding: '8px 0',
  },
  placeholderMessage: {
    fontStyle: 'italic',
  },
  urlOpenIcon: {
    position: 'absolute',
    top: '50%',
    right: 12,
    transform: 'translateY(-50%)',
    color: auroMetalSaurus,
    ':hover': {
      color: squidInk,
    },
  },
  actionBarSpacing: {
    marginTop: 16,
  },
  mergeVerifiedButton: {
    fontWeight: 600,
    borderRadius: 6,
    backgroundColor: red,
    border: 'none',
    ':hover': {
      backgroundColor: red,
      opacity: 0.9,
    },
  },
  unverifyBrandsButton: {
    fontWeight: 600,
    borderRadius: 6,
  },
  mfrPanelGroup: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    alignItems: 'stretch',
  },
  noManufacturerIdBadge: {
    backgroundColor: lightRed,
    color: bootstrapDanger,
    fontWeight: 700,
    fontSize: '0.7rem',
    padding: '4px 8px',
    borderRadius: 4,
  },
});

DuplicateBrandsCard.propTypes = {
  sortedDuplicateEntries: PropTypes.array,
  duplicateBrandsList: PropTypes.array,
  brandsById: PropTypes.object,
  brandsLoading: PropTypes.bool,
  brandsInitialLoading: PropTypes.bool,
  currentBrandId: PropTypes.number,
  selectedBrandId: PropTypes.number,
  onSelectBrandId: PropTypes.func,
  onSaveChanges: PropTypes.func,
  onAfterMergeDuplicates: PropTypes.func,
  onUnverifyBrands: PropTypes.func,
  selectedBrand: PropTypes.object,
  selectedBrandLoading: PropTypes.bool,
  saving: PropTypes.bool,
  unverifying: PropTypes.bool,
  updatingStatus: PropTypes.bool,
  skipping: PropTypes.bool,
  manualDuplicateBrandIds: PropTypes.array,
  onAddManualDuplicate: PropTypes.func,
  onRemoveManualDuplicate: PropTypes.func,
};

export default DuplicateBrandsCard;
